import React, { useMemo, useEffect, useState } from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';

import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

import SociollaTable from '@components/sociolla/SociollaTable';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import ModalTier from '@components/setting/matrix-approval/ModalTier';
import Image from '@components/global/Image';

import { hasNoAccess, currency } from '@global/helpers';
import {
  createMatrixTier,
  updateMatrixTier,
  deleteMatrixTier,
} from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import useApi from '@/hooks/useApi';

const MatrixTier = (props) => {
  const [rules, setRules] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [modalTier, setModalTier] = useState(false);
  const [selectedTier, setSelectedTier] = useState(-1);
  const getCustomerClusterRule = useApi(MsAccount.getCustomerClusterRule);

  const dispatch = useDispatch();

  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);

  const header = useMemo(() => {
    const isSalesOrder = props.data.applicable_for == 'sales_order';
    const isReturnOrder = props.data.applicable_for == 'order_return_approval';
    const isSkpApproval = props.data.applicable_for == 'skp_approval';
    const isBudgetPocket = props.data.applicable_for == 'budget_pocket';
    const isRequestProductTester =
      props.data.applicable_for == 'request_product_tester';
    const isSalesActivity = props.data.applicable_for == 'sales_activity';
    const defaultWidth = isSalesOrder
      ? '8%'
      : isBudgetPocket
      ? '12%'
      : isReturnOrder || isSkpApproval
      ? '13%'
      : isRequestProductTester
      ? '12%'
      : isSalesActivity
      ? '12%'
      : hasAccess
      ? '15%'
      : '20%';
    const tableHeader = [
      // {
      //   label: 'Tier',
      //   data: 'tier',
      //   width: hasAccess ? '33%' : '33%',
      // },
      {
        label: 'Approver 1',
        data: 'approver1',
        width: defaultWidth,
      },
      {
        label: 'Approver 2',
        data: 'approver2',
        width: defaultWidth,
      },
      {
        label: 'Approver 3',
        data: 'approver3',
        width: defaultWidth,
      },
      {
        label: 'Approver 4',
        data: 'approver4',
        width: defaultWidth,
      },
      {
        label: 'Approver 5',
        data: 'approver5',
        width: defaultWidth,
      },
    ];

    if (isSalesOrder) {
      tableHeader.unshift(
        {
          label: 'Tier',
          data: 'tier',
          width: '12%',
        },
        {
          label: 'Min. Order',
          data: 'min_amount',
          type: 'price',
          width: '8%',
        },
        {
          label: 'Payment Overdue',
          data: 'payment_overdue_value',
          width: '7%',
        },
        {
          label: 'Market Type',
          data: 'market_type',
          width: '7%',
        },
        {
          label: 'Business Type',
          data: 'business_type',
          width: '15%',
        },
      );
    } else if (isReturnOrder || isSkpApproval || isRequestProductTester) {
      tableHeader.unshift(
        {
          label: 'Tier',
          data: 'tier',
          width: isRequestProductTester ? '8%' : '16%',
        },
        {
          label:
            isReturnOrder || isRequestProductTester
              ? 'Customer Market Type'
              : 'Type',
          data:
            isReturnOrder || isRequestProductTester
              ? 'market_type'
              : 'customer_type',
          width: '12%',
        },
      );
    } else if (isBudgetPocket) {
      tableHeader.unshift(
        {
          label: 'Tier',
          data: 'applicable_on',
          width: '10%',
        },
        {
          label: 'Budget',
          data: 'tier',
          width: '20%',
        },
      );
    } else if (isSalesActivity) {
      tableHeader.unshift(
        {
          label: 'Tier',
          data: 'tier_type_label',
          width: '10%',
        },
        {
          label: 'Created by',
          data: 'tier',
          width: '12%',
        },
      );
    } else {
      tableHeader.unshift({
        label: 'Tier',
        data: 'tier',
        width: '15%',
      });
    }

    if (hasAccess) {
      tableHeader.push({
        label: '',
        data: '',
        type: 'action',
        action: [
          {
            icon: require('@assets/img/icons/edit-grey.svg'),
            name: 'edit',
          },
          {
            icon: require('@assets/img/icons/trash-grey.svg'),
            name: 'remove',
          },
        ],
        width: '6%',
      });
    }
    return tableHeader;
  }, [hasAccess, props.data.applicable_for]);

  const callBackAction = (data) => {
    if (data.name === 'remove') {
      dispatch(
        showAlert({
          show: true,
          title: 'Remove Tier',
          description: 'This tier will be deleted. Do you want to proceed?',
          onCallback: (val) => removeTier(data.index),
        }),
      );
    } else if (data.name === 'edit') {
      setSelectedTier(data.index);
      setModalTier(true);
    }
  };

  const renderItem = ({ item, index }) => (
    <TouchableOpacity
      onPress={() => callBackAction({ name: 'edit', data: item, index })}
      key={'list-matrix-' + index}
      style={styles.tableRow}>
      <SociollaText type="medium" style={styles.itemName}>
        {item.tier}
      </SociollaText>
      <TouchableOpacity
        onPress={() => callBackAction({ name: 'remove', data: item, index })}
        style={styles.itemDelete}>
        <Image
          width={20}
          height={20}
          svg={true}
          source={require('@assets/img/icons/trash-grey.svg')}
          style={styles.itemDeleteIcon}
        />
      </TouchableOpacity>
      <View style={styles.row}>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval 1:</SociollaText>
          <SociollaText style={styles.itemValue}>{item.approver1}</SociollaText>
        </View>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval 2:</SociollaText>
          <SociollaText style={styles.itemValue}>{item.approver2}</SociollaText>
        </View>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval 3:</SociollaText>
          <SociollaText style={styles.itemValue}>{item.approver3}</SociollaText>
        </View>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval 4:</SociollaText>
          <SociollaText style={styles.itemValue}>{item.approver4}</SociollaText>
        </View>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval 5:</SociollaText>
          <SociollaText style={styles.itemValue}>{item.approver5}</SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  const onClose = () => {
    setSelectedTier(-1);
    setModalTier(false);
  };

  const getRole = (role) => {
    const roles = role.split('_');
    for (let i = 0; i < roles.length; i++) {
      roles[i] = roles[i][0].toUpperCase() + roles[i].substr(1);
    }
    return roles.join(' ');
  };

  const getRulesAmount = (details) => {
    return details.map((det) => {
      const max = currency(det.max_amount, true);
      const min = currency(det.min_amount, true);
      const market_type = det.customer_market_type?.code || '';
      return {
        ...det,
        tier: min + ' - ' + max,
        market_type,
        ...generateApprover(det.approver),
      };
    });
  };

  const getTierLableForSkp = (value) => {
    const obj = {
      skp_internal: 'SKP Internal',
      skp_external_pkp: 'SKP External PKP',
      skp_external_non_pkp: 'SKP External non PKP',
    };
    return obj[value];
  };

  const TIER_TYPE_LABEL = { add: 'Add', reschedule: 'Reschedule' };

  const getRulesRole = (details) => {
    return details.map((det) => {
      let tierLable = det.applicable_on;
      if (det.applicable_on.includes('skp')) {
        tierLable = getTierLableForSkp(det.applicable_on);
      }
      const tierTypeLabel =
        TIER_TYPE_LABEL[det.tier_type || 'add'] || det.tier_type || 'add';
      let market_type = det.customer_market_type?.code || '';
      const business_type = det.customer_market_type?.business_type?.length
        ? det.customer_market_type.business_type.map((bt) => bt.code).join(', ')
        : '';
      if (market_type == 'Select All') {
        market_type = 'All';
      }
      return {
        ...det,
        tier: tierLable,
        tier_type_label: tierTypeLabel,
        payment_overdue_value: det.payment_overdue
          ? 'Yes'
          : det.payment_overdue == false
          ? 'No'
          : '',
        market_type,
        business_type,
        ...generateApprover(det.approver),
      };
    });
  };

  const generateApprover = (approvers) => {
    const approver = {};
    for (let index = 0; index < 5; index++) {
      const app = approvers[index] || null;
      if (app) {
        approver['approver' + app.level] = getRole(app.role);
      } else {
        approver['approver' + (index + 1)] = '-';
      }
    }
    return approver;
  };

  const generateRulesData = async (rule) => {
    let rules = [];
    try {
      if (rule.type == 'amount') {
        rules = await getRulesAmount(rule.rule);
      } else {
        rules = await getRulesRole(rule.rule);
      }
      setMatrix({ ...props.data, rule: rules });
      setRules(rules);
    } catch (e) {
      console.log(e);
    }
  };

  const addTier = () => {
    // if (rules.length > 4) {
    //   Toast.show({
    //     text1: 'Maximum 5 tiers can be added',
    //     type: 'error',
    //   });
    //   return;
    // }
    setModalTier(true);
  };

  const setTier = async (data, approver) => {
    let tier = [];
    if (data.type == 'amount') {
      tier = await getRulesAmount([
        {
          ...data,
          approver,
        },
      ]);
    } else if (data.type == 'role') {
      tier = await getRulesRole([
        {
          ...data,
          approver,
          applicable_on: data.applicable_on.code || data.applicable_on,
          tier_type: data.tier_type || 'add',
        },
      ]);
    }
    const newData = tier[0];
    const newRules = JSON.parse(JSON.stringify(rules));
    if (selectedTier > -1) {
      newRules.splice(selectedTier, 1, newData);
    } else {
      newData['_id'] = '';
      newRules.push(newData);
    }
    await setRules([...newRules]);
    return newRules;
  };

  // Direct Remove
  const removeTier = async (index) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const tierId = rules[index]._id;
      const response = await deleteMatrixTier(props.data._id, tierId);
      if (response.status == 200) {
        const newRules = JSON.parse(JSON.stringify(rules));
        newRules.splice(index, 1);
        setRules([...newRules]);
        Toast.show({
          text1: 'Successfully deleted tier',
          type: 'success',
        });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (e) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'A system error occurred',
        type: 'error',
      });
    }
  };

  // Direct Create/Edit
  const updateTier = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const approver = data.approver.map((ap) => {
        return {
          level: ap.level,
          role: ap.role.code,
        };
      });
      const payload = { approver };
      let text1 = '';
      let response = null;
      if (data.type == 'amount') {
        payload.min_amount = Number(data.min_amount);
        payload.max_amount = Number(data.max_amount);
        if (props.data.applicable_for === 'budget_pocket') {
          payload.applicable_on = data.applicable_on;
        }
      } else if (data.type == 'role') {
        if (data.tier?.listing?.length) {
          payload.applicable_on = data.applicable_on.value;
        } else {
          if (props.data.applicable_for === 'customer_cluster') {
            payload.applicable_on = data.applicable_on.name;
          } else {
            payload.applicable_on = data.applicable_on.code;
          }
        }
        if (props.data.applicable_for === 'sales_activity') {
          payload.tier_type = data.tier_type || 'add';
        }
      }
      if (
        props.data.applicable_for === 'order_return_approval' ||
        props.data.applicable_for === 'sales_order' ||
        props.data.applicable_for === 'request_product_tester'
      ) {
        payload.customer_market_type = data.customer_market_type;
      }
      if (props.data.applicable_for === 'skp_approval') {
        payload.customer_type = data.customer_type;
      }
      if (props.data.applicable_for === 'sales_order') {
        if (data.min_amount && !isNaN(data.min_amount)) {
          payload.min_amount = Number(data.min_amount);
        }
        payload.payment_overdue = data.payment_overdue || false;
        payload.customer_market_type['business_type'] = (
          data.business_type || []
        ).map((bt) => {
          return { id: bt.id, code: bt.code };
        });
      }
      if (selectedTier > -1) {
        text1 = 'Successfully updated tier';
        const tierId = rules[selectedTier]._id;
        response = await updateMatrixTier(props.data._id, tierId, {
          rule: [payload],
        });
      } else {
        text1 = 'Successfully added tier';
        response = await createMatrixTier(props.data._id, { rule: [payload] });
      }
      const newData = JSON.parse(JSON.stringify(props.data));
      newData['rule'] = response.data.data.rule;
      generateRulesData(newData);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      onClose();
      Toast.show({ text1, type: 'success' });
    } catch (e) {
      let message = 'A system error occurred';
      if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: message,
        type: 'error',
      });
    }
  };

  // Temporary Remove
  // eslint-disable-next-line no-unused-vars
  const removeTierTemp = (index) => {
    const newRules = JSON.parse(JSON.stringify(rules));
    newRules.splice(index, 1);
    setRules([...newRules]);
    props.setRules(newRules);
  };

  // Temporary Create/Edit
  // eslint-disable-next-line no-unused-vars
  const handleTier = async (data) => {
    try {
      const approver = data.approver.map((ap) => {
        return {
          level: ap.level,
          role: ap.role.code,
        };
      });
      const newRules = setTier(data, approver);
      props.setRules(newRules);
      onClose();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (Object.entries(props.data).length) {
      generateRulesData(props.data);
      if (props.data.applicable_for === 'customer_cluster') {
        getCustomerClusterRule.request({
          params: {
            filter: { is_active: true },
          },
        });
      }
    }
    // eslint-disable-next-line react/prop-types
    props.matrixRef.current = addTier;
  }, [props.data]);

  return (
    <>
      {isWeb && (
        <TitleSubSection
          title="TIER SETTINGS"
          button={
            props.isView
              ? false
              : {
                  label: 'Add Tier',
                  callback: addTier,
                }
          }
        />
      )}
      {rules && rules.length > 0 ? (
        <SociollaTable
          name="matrix-rules"
          table={header}
          datas={rules}
          itemComponent={isWeb ? null : renderItem}
          scrollItem={true}
          topComponent={false}
          callBackAction={callBackAction}
          styleContainer={styles.container}
          hideSearch
        />
      ) : (
        <View style={styles.notFound}>
          <SociollaText style={styles.notFoundText}>No tier found</SociollaText>
        </View>
      )}
      {modalTier && (
        <ModalTier
          index={selectedTier}
          data={matrix}
          onClose={() => onClose()}
          handleTier={(data) => updateTier(data)}
          cluster={getCustomerClusterRule.data}
          show
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: isWeb ? 18 : 0,
    textTransform: 'capitalize',
  },
  flex: { flex: 1 },
  itemDelete: { position: 'absolute', top: 17, right: 20 },
  itemDeleteIcon: { height: 20, width: 20 },
  itemName: { paddingBottom: 10, fontSize: 16, color: Colors.black1 },
  itemTitle: { paddingBottom: 5, fontSize: 10, color: Colors.black5 },
  itemValue: { fontSize: 12, color: Colors.black2 },
  notFound: {
    alignItems: 'center',
    marginTop: isWeb ? 68 : 40,
    marginBottom: isWeb ? 68 : 0,
  },
  notFoundText: {
    fontSize: 14,
    color: Colors.black3,
    marginTop: 20,
  },
  row: { flexDirection: 'row', width: '100%' },
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minHeight: 93,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    padding: 16,
  },
});

MatrixTier.propTypes = {
  data: PropTypes.object,
  isView: PropTypes.bool,
  setRules: PropTypes.func,
};

MatrixTier.defaultProps = {
  data: {},
  isView: false,
  setRules: () => false,
};

export default MatrixTier;
